import { faFileLines, faPlus, faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { exhaustive } from "exhaustive"
import { isNull } from "lodash"
import { convertTextToInitials } from "Orders/Helpers"
import { ChangeEvent, FC, useEffect, useRef, useState } from "react"
import { v4 } from "uuid"
import { AbsolutCentered } from "../../AbsolutCentered/AbsolutCentered"
import { MeResponseAccountType } from "../../Auth/Auth.types"
import { useAuth } from "../../Auth/AuthContext"
import { useClient } from "../../Client/ClientAndUserProvider"
import {
	CoredinationCustomerInvoiceDeliveryMethod,
	coredinationInvoiceDeliveryMethodToHumanText,
} from "../../ClientPortal/ClientPortalNewConsumerModal/ClientPortalNewConsumerModal"
import { PenIcon, TrashIcon } from "../../Icons/Icon"
import { Loader } from "../../Loader/Loader"
import { API } from "../../network/API"
import { SbtH6 } from "../../Orders/Components/Text/SbtH6/SbtH6"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import { cls } from "../../Shared/cls"
import {
	AddEditConsumerTagModal,
	ConsumerTagModalMode,
	TagModalView,
} from "../AddEditConsumerTagModal/AddEditConsumerTagModal"
import { GetConsumerTag, GetConsumerTags } from "../CustomerPortalConsumerTags/CustomerPortalConsumerTags"
import { EditConsumerDetails } from "../EditConsumerDetails/EditConsumerDetails"
import style from "./CustomerPortalConsumerInformation.module.css"

export type GetConsumer = {
	id: string
	consumerDetails: CompanyConsumerDetails | PrivateConsumerDetails
	state: ConsumerState
	files: GetCustomerFile[]
	profilePictureUrl?: string
	type: GetConsumerType
	stats?: GetConsumerStats
	customerNumber?: string
	isNew: boolean
	flags: ConsumerFlag[]
	externalInformation?: ExternalIntegrationInformation[]
}

export type ExternalReadOnlyCoredinationInformation = {
	type: "ReadOnlyCoredination"
	invoiceDeliveryMethod?: CoredinationCustomerInvoiceDeliveryMethod | null
	termsOfPaymentDays?: number | null
}

export type ExternalIntegrationInformation = ExternalReadOnlyCoredinationInformation

export enum ConsumerFlag {
	SelfRegistered = "SelfRegistered",
	AutoGeneratedFromAnonymousOrder = "AutoGeneratedFromAnonymousOrder",
}

export type GetConsumerStats = {
	orderAmount: number
	userAmount: number
	inviteAmount: number
}

export enum GetConsumerType {
	Company = "Company",
	PrivatePerson = "PrivatePerson",
}

export enum ConsumerState {
	Active = "Active",
	Inactive = "Inactive",
	Removed = "Removed",
	Pending = "Pending",
	Denied = "Denied",
}

export enum GetCustomerFileUploadedByUserType {
	Consumer = "Consumer",
	Client = "Client",
}

type CustomerFile = {
	url: string
	originalFileName: string
	id: string
	uploadedByUserType: GetCustomerFileUploadedByUserType
}

type GetCustomerFileImage = CustomerFile & {
	type: "Image"
}

type GetCustomerFileDocument = CustomerFile & {
	type: "Document"
	fileType: string
}

type GetCustomerFile = GetCustomerFileImage | GetCustomerFileDocument

export type ConsumerDetails = {
	name: string
}

export type CompanyConsumerDetails = ConsumerDetails & {
	type: "CompanyConsumerDetails"
	orgNumber?: string
}

export type PrivateConsumerDetails = ConsumerDetails & {
	type: "PrivateConsumerDetails"
	personNumber?: string
}

type GetCustomerFileCustom = GetCustomerFile & {
	uploading?: boolean
	uploadFailed?: boolean
	tooBig?: boolean
}

type CustomerPortalConsumerInformationProps = {
	consumer: GetConsumer
	onInactivate: () => void
	onConsumerUpdated: () => void
	className?: string
	hideAttachments?: boolean
}

export function getFile(e: ChangeEvent<HTMLInputElement>, accept?: string[]): (File & { tooBig?: boolean }) | null {
	e.preventDefault()
	if (e.target.files && e.target.files.length > 0) {
		const file: (File & { tooBig?: boolean }) | undefined = e.target.files[0]
		//TODO Refactor, we shouldn't cast and modify the "file type" used above here, also the old null check is used after file.type is accessed so that could blow up as well.
		if (file == null) {
			return null
		}

		if (accept && !accept.includes(file.type)) {
			return null
		}
		const maxSize = 1000 * 1000 * 10
		if (!isNull(file)) {
			if (file.size > maxSize) {
				file.tooBig = true
				return file
			} else {
				return file
			}
		}
	}
	return null
}

export const CustomerPortalConsumerInformation: FC<CustomerPortalConsumerInformationProps> = ({
	consumer,
	onInactivate,
	onConsumerUpdated,
	className,
	hideAttachments,
}) => {
	const auth = useAuth()
	const client = useClient()
	const permissions = usePermissions()

	const files = useRef<GetCustomerFileCustom[] | null>(consumer?.files || null)
	const [, setTick] = useState(0)
	const [showPfpTooBig, setShowPfpTooBig] = useState(false)
	const [edit, setEdit] = useState(false)
	const prevConsumer = useRef<GetConsumer | null>(null)

	const [tags, setTags] = useState<GetConsumerTag[] | null>(null)

	const [addEditConsumerTag, setAddEditConsumerTag] = useState<"new" | GetConsumerTag | null>(null)

	useEffect(() => {
		if (consumer?.state === ConsumerState.Inactive && prevConsumer.current?.state === ConsumerState.Active) {
			onInactivate()
		}
		prevConsumer.current = consumer
		files.current = consumer?.files || null
		fetchConsumerTags()
		setTick((tick) => tick + 1)
	}, [consumer])

	function fetchConsumerTags() {
		if (!auth.IsLoggedInClient || !consumer) {
			return
		}

		setTags(null)

		API.get<GetConsumerTags>(`/customer-portal/consumer-tags-v1/${client.identifier}`, true).then(
			(res) => {
				const filtered = res.tags.filter((x) => x.consumerRefs.includes(consumer.id))
				setTags(filtered)
			},
			() => {
				setTags([])
			},
		)
	}

	function addFileForConsumer(e: ChangeEvent<HTMLInputElement>) {
		if (!permissions.isAllowed(PermissionAreaLocation.Consumer_Attachments_Create)) {
			return
		}

		const file = getFile(e)

		if (!file || !consumer) {
			return
		}
		const tempId = v4()
		files.current?.push({
			id: tempId,
			type: "Document",
			url: "",
			originalFileName: file.name,
			fileType: file.type,
			uploading: true,
			tooBig: file.tooBig,
			uploadedByUserType:
				auth.Me?.type === MeResponseAccountType.Client
					? GetCustomerFileUploadedByUserType.Client
					: GetCustomerFileUploadedByUserType.Consumer,
		})
		setTick((tick) => tick + 1)

		if (!file.tooBig) {
			uploadFileForConsumer(file, consumer, tempId)
		}
	}

	function addProfilePictureForConsumer(e: ChangeEvent<HTMLInputElement>, accept?: string[]) {
		if (!permissions.isAllowed(PermissionAreaLocation.Consumer_Information_Profile_Picture_Edit)) {
			return
		}

		const file = getFile(e, accept)

		if (!file || !consumer) {
			return
		}

		if (file.tooBig) {
			setShowPfpTooBig(true)
		} else {
			uploadProfilePictureForConsumer(file, consumer)
		}
	}

	function uploadFileForConsumer(file: File, customer: GetConsumer, tempId: string) {
		const newForm = new FormData()
		newForm.append("file", file)
		API.postWithRetries(
			`/customer-portal/consumers-v1/${client.identifier}/upload-file/${customer.id}`,
			newForm,
			undefined,
			30,
			"Raw",
		)
			.then(() => {
				onConsumerUpdated()
			})
			.catch(() => {
				if (files.current?.find((x) => x.id === tempId)) {
					files.current.find((x) => x.id === tempId)!.uploadFailed = true
				}
				setTick((tick) => tick + 1)
			})
	}

	function uploadProfilePictureForConsumer(file: File, customer: GetConsumer) {
		const newForm = new FormData()
		newForm.append("file", file)
		setShowPfpTooBig(false)
		API.postWithRetries(
			`/customer-portal/consumers-v1/${client.identifier}/profile-picture/${customer.id}`,
			newForm,
			undefined,
			10,
			"Raw",
		)
			.then(() => {
				onConsumerUpdated()
			})
			.catch(() => {
				setTick((tick) => tick + 1)
			})
	}

	function removeFileForConsumer(fileId: string) {
		if (!permissions.isAllowed(PermissionAreaLocation.Consumer_Attachments_Delete)) {
			return
		}

		if (!window.confirm("Är du säker att du vill ta bort filen?")) {
			return
		}

		const file = files.current?.find((x) => x.id === fileId)
		if (file) {
			file.uploading = true
		}
		setTick((tick) => tick + 1)
		API.delete(`/customer-portal/consumers-v1/${client.identifier}/delete-file/${consumer.id}/${fileId}`)
			.then(() => {
				onConsumerUpdated()
			})
			.catch(() => {
				setTick((tick) => tick + 1)
			})
	}

	function headerAndEditRow(): JSX.Element {
		let penIcon

		if (
			auth.IsLoggedInClient &&
			permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Information_Update) &&
			consumer?.state === ConsumerState.Active
		) {
			penIcon = (
				<PenIcon
					size={22}
					iconClassName={style.editIcon}
					onClick={() => {
						setEdit(true)
					}}
				/>
			)
		}

		return (
			<div className={style.headerAndEditRow}>
				<span className={style.headerText}>Kunduppgifter</span>
				<span>{penIcon}</span>
			</div>
		)
	}

	function profilePicRow(customer: GetConsumer) {
		const id = v4()
		return (
			<div className={style.profilePicRow}>
				<span className={style.profilePic}>
					{customer.profilePictureUrl ? (
						<img
							src={customer.profilePictureUrl}
							onClick={() => {
								const url = customer.profilePictureUrl
								if (url) {
									window.open(url, "_blank")
								}
							}}
							onError={() => {
								customer.profilePictureUrl = undefined
								setTick((tick) => tick + 1)
							}}
							alt="Profilbild"
						/>
					) : (
						<div className={style.noProfilePic}>{convertTextToInitials(customer.consumerDetails.name)}</div>
					)}
				</span>
				{customer.state === ConsumerState.Active &&
				permissions.isAllowed(PermissionAreaLocation.Consumer_Information_Profile_Picture_Edit) ? (
					<span style={{ display: "flex", flexDirection: "column" }}>
						<label htmlFor={id} className={style.uploadProfilePictureButton}>
							<span className={style.text}>Ladda upp profilbild</span>
							<span className={style.icon}>+</span>
						</label>
						<input
							id={id}
							style={{ display: "none" }}
							type="file"
							accept=".png,.jpeg,.jpg"
							multiple={true}
							onChange={(e) => {
								addProfilePictureForConsumer(e, ["image/png", "image/jpg", "image/jpeg"])
							}}
						/>
						{showPfpTooBig ? (
							<div style={{ color: "var(--invalid-color)", fontWeight: 500 }}>För stor bild, max 10MB</div>
						) : null}
					</span>
				) : null}
			</div>
		)
	}

	function uploadedFiles() {
		if (!permissions.isAllowed(PermissionAreaLocation.Consumer_Attachments_View)) {
			return null
		}

		return (
			<div className={style.uploadedFiles}>
				<div className={style.header}>Uppladdade bilagor</div>
				<div className={style.filesContainer}>
					{files.current?.map((file, index) => {
						let iconElement

						if (file.tooBig || file.uploadFailed) {
							iconElement = (
								<div className={style.spinner}>
									<FontAwesomeIcon
										icon={faXmark}
										style={{ color: "var(--invalid-color)" }}
										onClick={() => {
											files.current?.splice(index, 1)
											setTick((tick) => tick + 1)
										}}
									/>
								</div>
							)
						} else if (file.uploading) {
							iconElement = (
								<div className={style.spinner}>
									<FontAwesomeIcon icon={faSpinner} spin={true} />
								</div>
							)
						} else {
							if (
								!permissions.isAllowed(PermissionAreaLocation.Consumer_Attachments_Delete) ||
								(file.uploadedByUserType === GetCustomerFileUploadedByUserType.Client &&
									auth.Me?.type !== MeResponseAccountType.Client)
							) {
								iconElement = null
							} else {
								iconElement = (
									<TrashIcon
										size={22}
										className={style.trashIcon}
										onClick={(e) => {
											e?.stopPropagation()
											removeFileForConsumer(file.id)
										}}
									/>
								)
							}
						}

						return (
							<div
								key={v4()}
								className={style.fileContainer}
								onClick={() => {
									if (!file.uploading) {
										window.open(file.url, "_blank")
									}
								}}>
								{iconElement}
								{file.type === "Image" ? (
									<img
										alt={file.originalFileName}
										className={style.image}
										key={file.url}
										src={file.url}
										style={{ marginBottom: "5px" }}
									/>
								) : (
									<div
										key={file.url}
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											marginBottom: "5px",
										}}>
										<FontAwesomeIcon
											style={{
												color: "var(--module-box-icon-color)",
												width: "70%",
												height: "70%",
											}}
											icon={faFileLines}
										/>
									</div>
								)}
								{file.tooBig ? (
									<div
										style={{
											color: "var(--invalid-color)",
											fontWeight: 500,
											fontSize: "14px",
										}}>
										För stor fil, max 10MB
									</div>
								) : file.uploadFailed ? (
									<div
										style={{
											color: "var(--invalid-color)",
											fontWeight: 500,
											fontSize: "14px",
										}}>
										Uppladdning misslyckades
									</div>
								) : (
									<span className={cls(style.fileName, style.twoLineClamp)} title={file.originalFileName}>
										{file.originalFileName}
									</span>
								)}
							</div>
						)
					})}
				</div>
			</div>
		)
	}

	function consumerTagsSection() {
		if (!auth.IsLoggedInClient || !tags) {
			return null
		}

		return (
			<div className={style.tagsSection}>
				<div className={style.headerText}>Taggar</div>
				<div className={style.consumerTagsWrapper}>
					{tags.map((tag) => {
						return exhaustive(tag, "type", {
							Generic: (it) => {
								return (
									<div
										key={it.id}
										className={style.consumerTag}
										onClick={() => {
											setAddEditConsumerTag(it)
										}}>
										{it.text}
									</div>
								)
							},
						})
					})}
					<div
						className={style.addTagIcon}
						onClick={() => {
							setAddEditConsumerTag("new")
						}}>
						<FontAwesomeIcon icon={faPlus} />
					</div>
				</div>
			</div>
		)
	}

	function addEditConsumerTagModalContent() {
		if (
			!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Information_Update) ||
			!consumer ||
			addEditConsumerTag === null
		) {
			return null
		}

		let mode: ConsumerTagModalMode = {
			type: "ConsumerView",
			consumerId: consumer.id,
			defaultView: TagModalView.SelectExistingTags,
			tag: null,
		}

		if (addEditConsumerTag !== "new") {
			mode.tag = addEditConsumerTag
		}

		return (
			<AddEditConsumerTagModal
				mode={mode}
				onClose={() => {
					setAddEditConsumerTag(null)
				}}
				onDone={() => {
					fetchConsumerTags()
					setAddEditConsumerTag(null)
				}}
			/>
		)
	}

	function externalInformationCoredinationSection() {
		if (!auth.IsLoggedInClient || !client.hasFullCoredinationIntegrationEnabled()) {
			return null
		}

		const coredinationInfo: ExternalReadOnlyCoredinationInformation | undefined = consumer.externalInformation?.find(
			(x) => x.type === "ReadOnlyCoredination",
		)

		if (!coredinationInfo) {
			return null
		}

		return (
			<div className={cls(style.wrapper, className)}>
				<div className={style.headerAndEditRow} style={{ marginBottom: "20px" }}>
					<span className={style.headerText}>Extern integrerings-information</span>
					<span></span>
				</div>
				<SbtH6 styles={{ marginBottom: "10px" }}>
					Denna information går endast att ändra via coredination för tillfället!
				</SbtH6>
				<div className={style.textInputWithLabel}>
					<label>Leveransmetod för faktura?</label>
					<span className={style.textAsInput}>
						{coredinationInfo?.invoiceDeliveryMethod
							? coredinationInvoiceDeliveryMethodToHumanText(coredinationInfo?.invoiceDeliveryMethod)
							: ""}
					</span>
				</div>
				<div className={style.textInputWithLabel}>
					<label>Antal dagar för betalning</label>
					<span className={style.textAsInput}>{coredinationInfo?.termsOfPaymentDays}</span>
				</div>
			</div>
		)
	}

	if (!permissions.isAllowed(PermissionAreaLocation.Consumer_Information_View)) {
		return null
	}

	return (
		<>
			{consumer && edit && auth.IsLoggedInClient ? (
				<EditConsumerDetails
					consumer={consumer}
					onClose={() => setEdit(false)}
					onDone={() => {
						setEdit(false)
						onConsumerUpdated()
					}}
				/>
			) : null}
			{addEditConsumerTagModalContent()}
			<div style={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%" }}>
				<div className={cls(style.wrapper, className)}>
					{!consumer ? (
						<AbsolutCentered>
							<Loader />
						</AbsolutCentered>
					) : (
						<>
							{headerAndEditRow()}
							{profilePicRow(consumer)}
							{consumer.state === ConsumerState.Inactive ? (
								<div style={{ display: "flex", justifyContent: "flex-end" }}>
									<div className={style.headerText} style={{ fontSize: "22px" }}>
										Inaktiverad kund
									</div>
								</div>
							) : consumer.state === ConsumerState.Denied ? (
								<div style={{ display: "flex", justifyContent: "flex-end" }}>
									<div className={style.headerText} style={{ fontSize: "22px" }}>
										Nekad kund
									</div>
								</div>
							) : (
								<div style={{ display: "flex", justifyContent: "flex-end" }}>
									<div className={cls(style.headerText, style.consumerTypeText)}>
										{consumer.consumerDetails.type === "CompanyConsumerDetails"
											? "Företagskund"
											: "Privatpersonskund"}
									</div>
								</div>
							)}
							<hr style={{ marginTop: "5px", marginBottom: "15px" }} />
							{consumer.flags.includes(ConsumerFlag.SelfRegistered) && auth.IsLoggedInClient ? (
								<div className={style.headerText} style={{ fontSize: "18px", marginBottom: "15px" }}>
									Självregistrerad kund
								</div>
							) : null}
							<div className={style.textInputWithLabel}>
								<label>
									{consumer.consumerDetails.type === "CompanyConsumerDetails" ? "Företagsnamn" : "Namn"}
								</label>
								<span className={style.textAsInput}>{consumer.consumerDetails.name}</span>
							</div>
							<div className={style.textInputWithLabel}>
								<label>
									{consumer.consumerDetails.type === "CompanyConsumerDetails"
										? "Organisationsnummer"
										: "Personnummer"}
								</label>
								{consumer.consumerDetails.type === "CompanyConsumerDetails" ? (
									<span className={style.textAsInput}>{consumer.consumerDetails.orgNumber}</span>
								) : (
									<span className={style.textAsInput}>{consumer.consumerDetails.personNumber}</span>
								)}
							</div>
							<div className={style.textInputWithLabel}>
								<label>Kundnummer</label>
								<span className={style.textAsInput}>{consumer.customerNumber}</span>
							</div>
							{consumerTagsSection()}

							{!hideAttachments ? (
								<>
									<hr style={{ marginTop: "30px", marginBottom: "30px" }} />
									{uploadedFiles()}
									{consumer.state === ConsumerState.Active &&
									permissions.isAllowed(PermissionAreaLocation.Consumer_Attachments_Create) ? (
										<div className={style.fileInput}>
											<label htmlFor={"uploadCustomerFile"}>
												<span>Ladda upp bilaga</span>
												<span>+</span>
											</label>

											<input
												id={"uploadCustomerFile"}
												type="file"
												multiple={false}
												onChange={(e) => {
													addFileForConsumer(e)
												}}
											/>
										</div>
									) : null}
								</>
							) : null}
						</>
					)}
				</div>
				{consumer ? externalInformationCoredinationSection() : null}
			</div>
		</>
	)
}
